// Context for managing time display settings
// Provides configurable "hours per day" setting for time calculations
// and "quick win hours" threshold for Quick Wins tab

import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { waitForServerAuth } from '../services/authReadyService';

const STORAGE_KEY = 'time_settings_hours_per_day';
const QUICK_WIN_STORAGE_KEY = 'time_settings_quick_win_hours';
const DEFAULT_HOURS_PER_DAY = 24;
const DEFAULT_QUICK_WIN_HOURS = 4;

const TimeSettingsContext = createContext();

/**
 * Hook to access time settings
 * @returns {Object} { hoursPerDay, setHoursPerDay, quickWinHours, setQuickWinHours }
 */
export const useTimeSettings = () => {
    const context = useContext(TimeSettingsContext);
    if (!context) {
        throw new Error('useTimeSettings must be used within TimeSettingsProvider');
    }
    return context;
};

/**
 * Get current hours per day setting (for use in utility files)
 * @returns {number} Hours per day (1-24)
 */
export const getHoursPerDay = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const value = parseInt(stored, 10);
            if (value >= 1 && value <= 24) {
                return value;
            }
        }
    } catch (error) {
        console.error('Error reading hours per day from localStorage:', error);
    }
    return DEFAULT_HOURS_PER_DAY;
};

/**
 * Get current quick win hours setting (for use in utility files)
 * @returns {number} Quick win hours threshold (1-999)
 */
export const getQuickWinHours = () => {
    try {
        const stored = localStorage.getItem(QUICK_WIN_STORAGE_KEY);
        if (stored) {
            const value = parseInt(stored, 10);
            if (value >= 1 && value <= 999) {
                return value;
            }
        }
    } catch (error) {
        console.error('Error reading quick win hours from localStorage:', error);
    }
    return DEFAULT_QUICK_WIN_HOURS;
};

export const TimeSettingsProvider = ({ children }) => {
    const [hoursPerDay, setHoursPerDayState] = useState(() => getHoursPerDay());
    const [quickWinHours, setQuickWinHoursState] = useState(() => getQuickWinHours());

    // Track initial mount to skip redundant server saves
    const isInitialMount = useRef(true);

    // Persist hoursPerDay to localStorage and server whenever value changes
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, hoursPerDay.toString());

            // Skip server save on initial mount (UserDataSynchronizer handles initial sync)
            if (isInitialMount.current) {
                return;
            }

            // Also save to server for cross-browser persistence (only if logged in)
            const { hasToken } = require('../services/tokenStorage');
            if (!hasToken()) return; // Skip server save if not authenticated

            const saveToServer = async () => {
                try {
                    await waitForServerAuth(); // Wait for server login to complete
                    const apiService = (await import('../services/apiService')).default;
                    await apiService.saveSettingToServer('time_settings_hours_per_day', hoursPerDay);
                    console.log('📡 Hours per day setting saved to server');
                } catch (error) {
                    console.error('Failed to save hours per day to server:', error);
                }
            };
            saveToServer();
        } catch (error) {
            console.error('Error saving hours per day to localStorage:', error);
        }
    }, [hoursPerDay]);

    // Persist quickWinHours to localStorage and server whenever value changes
    useEffect(() => {
        try {
            localStorage.setItem(QUICK_WIN_STORAGE_KEY, quickWinHours.toString());

            // Mark initial mount as done after both effects have run
            if (isInitialMount.current) {
                isInitialMount.current = false;
                return;
            }

            // Also save to server for cross-browser persistence (only if logged in)
            const { hasToken } = require('../services/tokenStorage');
            if (!hasToken()) return; // Skip server save if not authenticated

            const saveToServer = async () => {
                try {
                    await waitForServerAuth(); // Wait for server login to complete
                    const apiService = (await import('../services/apiService')).default;
                    await apiService.saveSettingToServer('time_settings_quick_win_hours', quickWinHours);
                    console.log('📡 Quick win hours setting saved to server');
                } catch (error) {
                    console.error('Failed to save quick win hours to server:', error);
                }
            };
            saveToServer();
        } catch (error) {
            console.error('Error saving quick win hours to localStorage:', error);
        }
    }, [quickWinHours]);

    /**
     * Set hours per day with validation
     * @param {number} value - Hours per day (1-24)
     */
    const setHoursPerDay = (value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) {
            console.warn('Invalid hours per day value:', value);
            return;
        }

        // Clamp between 1 and 24
        const clampedValue = Math.max(1, Math.min(24, numValue));
        setHoursPerDayState(clampedValue);
    };

    /**
     * Set quick win hours with validation
     * @param {number} value - Quick win hours threshold (1-999)
     */
    const setQuickWinHours = (value) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) {
            console.warn('Invalid quick win hours value:', value);
            return;
        }

        // Clamp between 1 and 999
        const clampedValue = Math.max(1, Math.min(999, numValue));
        setQuickWinHoursState(clampedValue);
    };

    const value = {
        hoursPerDay,
        setHoursPerDay,
        quickWinHours,
        setQuickWinHours
    };

    return (
        <TimeSettingsContext.Provider value={value}>
            {children}
        </TimeSettingsContext.Provider>
    );
};
